import { Config } from "@itarget/shared";
import {
  SSO_CLIENT_ENTITY,
  buildSsoAuthorizeUrl,
  buildSsoLogoutUrl as buildSharedSsoLogoutUrl,
  buildSsoSessionUrl as buildSharedSsoSessionUrl,
  hasSsoAttempt,
  clearSsoAttempt,
  consumeSsoCallback,
  clearSsoCallbackCache,
  mapSsoTokenToEntityUser,
  createSsoState,
  createSsoPkce,
  ssoRedirectUri,
  readHashFromLocation,
  stripHashFromUrl,
  pathWithoutHash,
  SSO_ATTEMPT_KEY,
  SSO_STATE_KEY,
  SSO_RETURN_KEY,
  SSO_VERIFIER_KEY,
} from "@itarget/shared/src/utils/sso";

export {
  SSO_CLIENT_ENTITY,
  hasSsoAttempt,
  clearSsoAttempt,
  consumeSsoCallback,
  clearSsoCallbackCache,
  mapSsoTokenToEntityUser,
  ssoRedirectUri,
  readHashFromLocation,
  stripHashFromUrl,
  pathWithoutHash,
};

export function resolveAuthURL() {
  if (Config.authURL) {
    return Config.authURL;
  }
  const host = window.location.hostname;
  if (host === "localhost" || host.indexOf("127.0.0.1") === 0) {
    return "http://localhost";
  }
  if (host.indexOf("homologacao") !== -1) {
    return "https://auth.homologacao.itarget.com.br";
  }
  if (host.indexOf("teste") !== -1) {
    return "https://auth.teste.itarget.com.br";
  }
  return "https://auth.itarget.com.br";
}

export function isSsoEnabled() {
  if (typeof Config.ssoEnabled === "boolean") {
    return Config.ssoEnabled;
  }
  const clients = Config.ssoEnabledClients || [];
  return clients.indexOf(Config.client) !== -1;
}

function appendHiddenInput(form, name, value) {
  const input = document.createElement("input");
  input.type = "hidden";
  input.name = name;
  input.value = value == null ? "" : String(value);
  form.appendChild(input);
}

/**
 * Com hash: POST /login/hash (cookie bridge) — credencial fora da URL.
 * Sem hash: GET /authorize (fluxo SSO normal).
 */
export async function redirectToSsoAuthorize(returnPath, costCenterId, hash) {
  const state = createSsoState();
  const redirectUri = ssoRedirectUri();
  const pkce = await createSsoPkce();
  const hashLink = hash || readHashFromLocation(window.location) || "";
  const safeReturn = pathWithoutHash(returnPath || "/");
  window.sessionStorage.setItem(SSO_STATE_KEY, state);
  window.sessionStorage.setItem(SSO_RETURN_KEY, safeReturn);
  window.sessionStorage.setItem(SSO_ATTEMPT_KEY, "1");
  window.sessionStorage.setItem(SSO_VERIFIER_KEY, pkce.verifier);

  const authURL = resolveAuthURL();
  const authorizeParams = {
    authURL,
    clientId: SSO_CLIENT_ENTITY,
    redirectUri,
    state,
    tenant: Config.client,
    codeChallenge: pkce.challenge,
    codeChallengeMethod: pkce.method,
    costCenterId,
  };

  if (!hashLink) {
    window.location.assign(buildSsoAuthorizeUrl(authorizeParams));
    return;
  }

  const bridgeUrl = new URL(`${String(authURL).replace(/\/$/, "")}/login/hash`);
  bridgeUrl.searchParams.set("clientId", SSO_CLIENT_ENTITY);
  bridgeUrl.searchParams.set("redirectUri", redirectUri);
  bridgeUrl.searchParams.set("state", state);
  bridgeUrl.searchParams.set("codeChallenge", pkce.challenge);
  bridgeUrl.searchParams.set("codeChallengeMethod", pkce.method);
  if (Config.client) {
    bridgeUrl.searchParams.set("tenant", Config.client);
  }
  if (costCenterId) {
    bridgeUrl.searchParams.set("costCenterId", String(costCenterId));
  }

  const form = document.createElement("form");
  form.method = "POST";
  form.action = bridgeUrl.toString();
  form.style.display = "none";
  appendHiddenInput(form, "hash", hashLink);
  document.body.appendChild(form);
  form.submit();
}

export function buildSsoLogoutUrl(postLogoutRedirectUri) {
  return buildSharedSsoLogoutUrl({
    authURL: resolveAuthURL(),
    postLogoutRedirectUri,
    tenant: Config.client,
  });
}

export function buildSsoSessionUrl() {
  return buildSharedSsoSessionUrl({
    authURL: resolveAuthURL(),
    tenant: Config.client,
  });
}

export async function fetchSsoAuthenticated() {
  const response = await fetch(buildSsoSessionUrl(), {
    credentials: "include",
  });
  if (!response.ok) {
    return null;
  }
  const body = await response.json();
  if (!body || !body.data) {
    return false;
  }
  return body.data.authenticated === true;
}

export function hashLinkFromUserPayload(payload) {
  if (!payload || typeof payload !== "object") {
    return "";
  }
  const data = payload.data && typeof payload.data === "object" ? payload.data : payload;
  return (
    data.hash_link_senha ||
    data.hashLinkPassword ||
    data.hashLinkSenha ||
    ""
  );
}
